import { tagRecords } from '../sim/tags';
import { taggedAtSite } from './tagged';
import { BASINS, REGIONS, region, type RegionId } from '../sim/data/oceans';
import { SITES, reefOf, type SiteId } from '../sim/data/sites';
import { exchangePairs } from '../sim/currents';
import { reefCover } from '../sim/coral';
import { ISLAND_NAMES, REEF_ISLANDS } from '../sim/reef';
import { epochDayOf } from '../sim/calendar';
import { heatStressToday } from '../sim/systems/heatStress';
import type { World } from '../sim/types';
import { signed } from './format';
import { REGION_MIDDLES, REGION_ROWS_1DEG, WORLD_LAND } from './data/worldMap';
import type { humpbackWays } from './migrants';
import { rightWhaleText } from './rightwhales';
import { blueMarlinText } from './marlin';
import { leatherbackText } from './leatherback';
import { grayWhaleText } from './graywhales';
import { speciesOf } from '../sim/species';
import { LIMIT_GROUPS, LIMIT_NAMES, type Management } from '../sim/management';

/**
 * The world map (M98, the brief's Level 1 and section 7): the seven seas' sites, the twelve regions the simulation
 * steps, the regions' exchange by the currents (NOAA's drifters, `currents.ts`), Natural Earth's land; a site picked
 * to see what it is and, where its aquarium is built, to visit it. The arithmetic is pure and tested in node
 * (`tests/worldMap.test.ts`); `drawWorldMap` paints it on a canvas.
 */

/** The seven seas' sites, in the brief's order (`AQUARIUM_REDESIGN.md` section 4). */
export const WORLD_SITES: readonly SiteId[] = ['maui_nui', 'vavau', 'gulf_of_maine', 'cape_peninsula', 'ningaloo', 'antarctic_peninsula', 'svalbard'];

/** Each site's habitats and wildlife as the brief names them (section 4): what its aquarium is for, not what is built. */
const SITE_HABITATS: Readonly<Record<SiteId, string>> = {
  maui_nui: 'tropical reef and sand channels; volcanic slope; sunlit open water; twilight; deep',
  vavau: 'tropical reefs, rocky drop-offs, blue water',
  gulf_of_maine: 'cold-temperate shelf, rocky reef, plankton-rich open water',
  cape_peninsula: 'giant kelp forest, rocky reef, cold upwelling',
  ningaloo: 'fringing reef, sandy reef habitats, open ocean',
  antarctic_peninsula: 'seasonal sea ice, cold shelf, krill-rich water, deep polar ocean',
  svalbard: 'seasonal sea ice, cold shelf, Arctic water',
};

/** A point on the map, pixels from its top left, for a map `width` by `height` (the whole globe, plate carrée). */
export function project(latDeg: number, lonDeg: number, width: number, height: number): [number, number] {
  const lon = ((((lonDeg + 180) % 360) + 360) % 360) - 180;
  return [((lon + 180) / 360) * width, ((90 - latDeg) / 180) * height];
}

/** A site's state, said plainly: the player's, visitable (its seafloor built), or not built yet. */
export type SiteStatus = 'yours' | 'visitable' | 'unbuilt';

export function siteStatus(siteId: SiteId, ownSiteId: SiteId | undefined): SiteStatus {
  if (siteId === ownSiteId) return 'yours';
  return SITES[siteId].seafloor ? 'visitable' : 'unbuilt';
}

/**
 * A site today as the game knows it (M104, the brief's Level 1: "each ocean's conservation and management
 * information", and section 8's selector, "basin, name, ecological status"): its basin; its ecological status, the
 * simulation's (the player's own reef's live coral and heat stress, every site's region's sea against its normal);
 * and its management, the game's: the player's reach and the fishing the simulation carries. The real protections
 * of each place are not given: no source on them has been read (open).
 */
export function siteNow(world: World, siteId: SiteId): [string, string][] {
  const def = SITES[siteId];
  const r = region(def.regionId);
  const basin = BASINS.find((b) => b.id === r.basin)?.name ?? r.basin;
  const env = world.regions.find((x) => x.id === def.regionId)?.env;
  const sea = env ? `its region's sea ${env.seaSurfaceC.toFixed(1)} °C at the surface, ${signed(env.seaSurfaceAnomalyC, 1)} °C from its normal (World Ocean Atlas 2023 and NOAA ERSST v5)` : '';
  const own = world.sanctuaries.find((s) => s.siteId === siteId);
  const reef = own ? reefOf(own) : null;
  const heat = own ? heatStressToday(own, epochDayOf(world)) : null;
  const coral = own && reef && own.coral
    ? `${reefCover(reef, own.coral).toFixed(1)}% of its reef's floor to 30 m live coral${heat ? `, ${heat.level === 'No stress' ? 'no heat stress' : `heat stress: ${heat.level.toLowerCase()}`}, ${heat.dhw.toFixed(1)} degree heating weeks (NOAA Coral Reef Watch's measure)` : ''}; `
    : '';
  // The populations of no sanctuary, on the site of each region the registry gives them (M160; M107's right whales,
  // M108's blue marlin, M111's leatherbacks, M112's gray whales).
  const migrants: [string, string][] = MIGRANT_ROWS.filter(([id]) => speciesOf(id).regions?.includes(def.regionId))
    .map(([, label, text]) => [label, text(world)]);
  const ownNothing = migrants.length > 0 ? `nothing of its own simulated yet but its sea's ${migrants.map(([name]) => name.toLowerCase()).join(' and ')}, below` : 'nothing of its own simulated yet';
  // The player's tagged animals and schools here (M148), their last place their sanctuary's.
  const tagged = taggedAtSite(tagRecords(world), siteId);
  return [
    ['Basin', `the ${basin}`],
    ...(tagged ? [['Tagged', tagged] as [string, string]] : []),
    ['Status', own ? `${coral}${sea}` : `${sea}; ${ownNothing}`],
    ...migrants,
    ['Management', own
      ? `yours: ${own.fish ? `its reef fish fished under ${limitsOn(own)}, ${protectedOn(own)}, ${patrolledOn(own)}. Its real reserves, Kahekili's among them, are not given` : 'your tools to protect it are not built yet. Its real protections are not given: no source on them has been read'}`
      : 'outside your sanctuaries: your management does not reach it. Its real protections are not given: no source on them has been read'],
  ];
}

/**
 * The size and bag limits a sanctuary's reef fish are fished under (M170; review M181: the row had still said them fished
 * at the main islands' rates, M165's, and no source read on their rules): Hawaiʻi's, or the player's own beside them on
 * the groups named (an option other than Hawaiʻi's, the first).
 */
function limitsOn(s: { management: Management | null }): string {
  const own = LIMIT_GROUPS.filter((g) => (s.management?.limits[g].bag ?? 0) !== 0 || (s.management?.limits[g].size ?? 0) !== 0).map((g) => LIMIT_NAMES[g]);
  return own.length > 0 ? `your own size and bag limits on ${own.join(', ')} and Hawaiʻi's on the rest (HAR 13-95 and 13-95.1)` : 'Hawaiʻi\'s size and bag limits (HAR 13-95 and 13-95.1)';
}

/** Where a sanctuary's patrols are closer than Hawaiʻi's officers' alone (M171), its islands named, or that none are. */
function patrolledOn(s: { management: Management | null }): string {
  const on = REEF_ISLANDS.filter((_, i) => (s.management?.patrols[i] ?? 0) > 0).map((island) => ISLAND_NAMES[island]);
  return on.length > 0 ? `patrols added on ${on.join(', ')}` : 'no patrols added beside Hawaiʻi\'s officers';
}

/** Where a sanctuary's herbivores are protected (M165), its islands named, or that none are. */
function protectedOn(s: { management: { herbivoreProtection: readonly boolean[] } | null }): string {
  const on = REEF_ISLANDS.filter((_, i) => s.management?.herbivoreProtection[i] === true).map((island) => ISLAND_NAMES[island]);
  return on.length > 0 ? `its parrotfish, kala, nenue and manini protected on ${on.join(', ')}` : 'no island\'s herbivores protected yet';
}

/** The migrants' rows on the map, in this order, each where the species registry places it (`SpeciesRecord.regions`). */
const MIGRANT_ROWS: readonly (readonly [string, string, (world: World) => string])[] = [
  ['rightWhale', 'Right whales', rightWhaleText],
  ['blueMarlin', 'Blue marlin', blueMarlinText],
  ['leatherback', 'Leatherbacks', leatherbackText],
  ['grayWhale', 'Gray whales', grayWhaleText],
];

/** What the map says of a site picked: its name, its sea, its habitats, what is built of it, and today's rows (`siteNow`). */
export function siteCard(siteId: SiteId, ownSiteId: SiteId | undefined, now: [string, string][] = []): { title: string; rows: [string, string][]; status: SiteStatus } {
  const def = SITES[siteId];
  const status = siteStatus(siteId, ownSiteId);
  const built = {
    yours: 'Your sanctuary: its aquarium is the one you run.',
    visitable: 'Not one of your sanctuaries. Its aquarium is built from its real seafloor (GEBCO), its water and light its region\'s; no wildlife is simulated there yet, so none is drawn.',
    unbuilt: 'Not one of your sanctuaries, and nothing of its aquarium is built yet: its sea is simulated at its regions\' scale.',
  }[status];
  return {
    title: def.name.replace(/ Sanctuary$/, ''),
    status,
    rows: [
      ['Sea', region(def.regionId).name],
      ['Where', `${def.site.placeName}, ${Math.abs(def.site.latDeg).toFixed(1)}°${def.site.latDeg >= 0 ? 'N' : 'S'} ${Math.abs(def.site.lonDeg).toFixed(1)}°${def.site.lonDeg >= 0 ? 'E' : 'W'}${def.seafloor ? '' : ' (approximate)'}`],
      ['Habitats', SITE_HABITATS[siteId]],
      ['Now', built],
      ...now,
    ],
  };
}

/** The site whose marker is within `withinPx` of a point on the map, the nearest; null for none. */
export function siteAt(x: number, y: number, width: number, height: number, withinPx: number): SiteId | null {
  let best = withinPx;
  let found: SiteId | null = null;
  for (const id of WORLD_SITES) {
    const [sx, sy] = project(SITES[id].site.latDeg, SITES[id].site.lonDeg, width, height);
    const d = Math.hypot(sx - x, sy - y);
    if (d <= best) {
      best = d;
      found = id;
    }
  }
  return found;
}

/**
 * The regions' exchange as lines between their middles, each pair once: its width by the mean of its two rates on
 * a log scale, 1 to 4 px (a rendering choice), split where it crosses the map's edge, the shorter way round.
 */
export function exchangeLines(width: number, height: number): { a: RegionId; b: RegionId; px: number; segments: [number, number, number, number][] }[] {
  const pairs = exchangePairs();
  const means = pairs.map((p) => Math.log10((p.ab + p.ba) / 2));
  const [lo, hi] = [Math.min(...means), Math.max(...means)];
  return pairs.map((p, k) => {
    const [la, oa] = REGION_MIDDLES[p.a];
    const lb = REGION_MIDDLES[p.b][0];
    let ob = REGION_MIDDLES[p.b][1];
    if (ob - oa > 180) ob -= 360;
    if (oa - ob > 180) ob += 360;
    const [x0, y0] = [((oa + 180) / 360) * width, ((90 - la) / 180) * height];
    const [x1, y1] = [((ob + 180) / 360) * width, ((90 - lb) / 180) * height];
    const segments: [number, number, number, number][] = [[x0, y0, x1, y1]];
    if (x1 < 0 || x1 > width) segments.push([x0 + (x1 < 0 ? width : -width), y0, x1 + (x1 < 0 ? width : -width), y1]);
    return { a: p.a, b: p.b, px: 1 + (3 * ((means[k] as number) - lo)) / Math.max(1e-9, hi - lo), segments };
  });
}

/** The regions' raster as cells: each 1° cell's region, or null; row 0 at 89.5°N, column 0 at 179.5°W. */
export function regionCells(): (RegionId | null)[][] {
  return REGION_ROWS_1DEG.map((row) => {
    const out: (RegionId | null)[] = [];
    for (const [, ch, n] of row.matchAll(/([a-z.])(\d*)/g)) {
      const id = ch === '.' ? null : (REGIONS[(ch as string).charCodeAt(0) - 97]?.id ?? null);
      for (let i = 0; i < (n ? Number(n) : 1); i++) out.push(id);
    }
    return out;
  });
}

/** Each region's tint on the map (a rendering choice: the basins' bands told apart, the high latitudes paler). */
const TINTS: Readonly<Record<RegionId, string>> = {
  north_pacific_low: '#12456e', north_pacific_high: '#1d5b82', south_pacific_low: '#103f66', south_pacific_high: '#1b557c',
  north_atlantic_low: '#164a6c', north_atlantic_high: '#21607f', south_atlantic_low: '#134466', south_atlantic_high: '#1e5a7a',
  indian_low: '#11486a', indian_high: '#1c5d80', southern: '#2a6a8a', arctic: '#2d7090',
};

/** Paints the map on a canvas's context, `width` by `height` pixels: the regions, the land, the currents' lines, the migrants' ways (M99), the sites. */
/** The ring round a site with living tagged animals (M148), its radius and its color (the interface's choices). */
const TAGGED_RING = { radius: 9.5, width: 2, color: '#8ef0a0' } as const;

export function drawWorldMap(ctx: CanvasRenderingContext2D, width: number, height: number, ownSiteId: SiteId | undefined, picked: SiteId | null, ways: ReturnType<typeof humpbackWays> = [],
  tagged: readonly SiteId[] = []): void {
  ctx.fillStyle = '#0a2033';
  ctx.fillRect(0, 0, width, height);
  const cells = regionCells();
  const [cw, ch] = [width / 360, height / 180];
  cells.forEach((row, r) => row.forEach((id, c) => {
    if (!id) return;
    ctx.fillStyle = TINTS[id];
    ctx.fillRect(c * cw, r * ch, cw + 0.5, ch + 0.5);
  }));
  ctx.fillStyle = '#4d5a3e';
  ctx.strokeStyle = '#6e7d58';
  ctx.lineWidth = 0.5;
  for (const ring of WORLD_LAND) {
    ctx.beginPath();
    for (let i = 0; i + 1 < ring.length; i += 2) {
      const [x, y] = project(ring[i + 1] as number, ring[i] as number, width, height);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(160, 220, 255, 0.55)';
  for (const line of exchangeLines(width, height)) {
    ctx.lineWidth = line.px;
    for (const [x0, y0, x1, y1] of line.segments) {
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    }
  }
  // The Hawaii humpbacks' ways to their feeding grounds, each ground marked (`migrants.ts`).
  ctx.strokeStyle = 'rgba(255, 178, 92, 0.85)';
  ctx.fillStyle = 'rgba(255, 178, 92, 0.95)';
  for (const way of ways) {
    ctx.lineWidth = way.px;
    for (const line of way.lines) {
      ctx.beginPath();
      line.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(way.end[0], way.end[1], 1.5 + way.px / 2, 0, 2 * Math.PI);
    ctx.fill();
  }
  ctx.font = `${Math.max(10, Math.round(width / 90))}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(220, 238, 250, 0.7)';
  for (const r of REGIONS) {
    const [la, lo] = REGION_MIDDLES[r.id];
    const [x, y] = project(la, lo, width, height);
    const text = r.name.replace(', low and mid latitudes', ', low').replace(', high latitudes', ', high').replace(', tropical and monsoon', ', tropical').replace(', south temperate', ', temperate');
    // Kept whole on the map: a region whose middle is near the date line is labeled inside the edge (M98's probe).
    const half = ctx.measureText(text).width / 2 + 2;
    ctx.fillText(text, Math.min(width - half, Math.max(half, x)), y);
  }
  for (const id of WORLD_SITES) {
    const [x, y] = project(SITES[id].site.latDeg, SITES[id].site.lonDeg, width, height);
    const status = siteStatus(id, ownSiteId);
    ctx.beginPath();
    ctx.arc(x, y, id === picked ? 7 : 5, 0, 2 * Math.PI);
    ctx.fillStyle = status === 'yours' ? '#ffd166' : status === 'visitable' ? '#7fd3ff' : '#c7d2db';
    ctx.fill();
    ctx.lineWidth = id === picked ? 2.5 : 1.5;
    ctx.strokeStyle = '#04131f';
    ctx.stroke();
    // A site with living tagged animals ringed (M148), their last place their sanctuary.
    if (tagged.includes(id)) {
      ctx.beginPath();
      ctx.arc(x, y, TAGGED_RING.radius, 0, 2 * Math.PI);
      ctx.lineWidth = TAGGED_RING.width;
      ctx.strokeStyle = TAGGED_RING.color;
      ctx.stroke();
    }
  }
}

/** The sites with living tagged animals, for the map's rings. */
export function taggedSites(world: World): SiteId[] {
  return [...new Set(world.tags.animals.filter((a) => !a.died).map((a) => a.siteId))];
}
